import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import CopyButton from "../../components/CopyButton";
import { ErrorBanner, Modal, PrimaryButton, SecondaryButton, TextArea, Field } from "../../components/crm/Form";
import DiagnosticReportEditor from "../../components/diagnostics/DiagnosticReportEditor";
import PhotoUploader from "../../components/diagnostics/PhotoUploader";
import PhotoGrid from "../../components/diagnostics/PhotoGrid";
import ReportAuditTrail from "../../components/diagnostics/ReportAuditTrail";
import ReportStatusBar from "../../components/diagnostics/ReportStatusBar";
import ShareLinkPanel from "../../components/diagnostics/ShareLinkPanel";
import OwnerAcknowledgmentPanel from "../../components/diagnostics/OwnerAcknowledgmentPanel";
import FlagToggleButton from "../../components/diagnostics/FlagToggleButton";
import NotificationsMiniFeed from "../../components/notifications/NotificationsMiniFeed";
import RMARelatedCard from "../../components/rma/RMARelatedCard";
import ReportPdfButton, {
  PrintButton,
} from "../../components/diagnostics/ReportPdfButton";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const OFFICE = ["owner", "ops"];

export default function DiagnosticReportPage() {
  const { id: woId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isOffice = OFFICE.includes(user?.role);

  const [wo, setWo] = useState(null);
  const [report, setReport] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [audit, setAudit] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenNote, setReopenNote] = useState("");

  // Local draft state for inputs (debounced commit on blur).
  const [draft, setDraft] = useState({
    findings_text: "",
    recommendations_text: "",
    owner_summary: "",
    parts_required: [],
    test_readings: [],
  });

  const isAssigned =
    wo &&
    (wo.assigned_lead_tech_user_id === user?.id ||
      wo.assigned_assistant_user_id === user?.id);

  const status = report?.status;
  const readOnly =
    !report ||
    status === "finalized" ||
    (status === "submitted" && !isOffice);

  const canSubmit =
    !!report && status === "draft" && (isOffice || isAssigned);
  const canFinalize = !!report && status === "submitted" && isOffice;
  const canReopen = !!report && status === "finalized" && isOffice;

  const loadAll = useCallback(async () => {
    setErr("");
    try {
      const woResp = await api.get(`/work-orders/${woId}`);
      setWo(woResp.data);

      let r;
      try {
        r = (await api.get(`/work-orders/${woId}/diagnostic-report`)).data;
      } catch (e2) {
        if (e2?.response?.status === 404) {
          // Auto-create draft so the page is immediately usable.
          r = (await api.post(`/work-orders/${woId}/diagnostic-report`)).data;
        } else {
          throw e2;
        }
      }
      setReport(r);
      setDraft({
        findings_text: r.findings_text || "",
        recommendations_text: r.recommendations_text || "",
        owner_summary: r.owner_summary || "",
        parts_required: r.parts_required || [],
        test_readings: r.test_readings || [],
      });

      const photosResp = await api.get(
        `/work-orders/${woId}/diagnostic-report/photos`
      );
      setPhotos(photosResp.data || []);

      try {
        const auditResp = await api.get(
          `/work-orders/${woId}/diagnostic-report/audit-log`
        );
        setAudit(auditResp.data || []);
      } catch {
        setAudit([]);
      }
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [woId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const commitField = useCallback(
    async (field, overrideValue) => {
      if (readOnly) return;
      const value =
        overrideValue !== undefined ? overrideValue : draft[field];
      if (
        report &&
        JSON.stringify(report[field]) === JSON.stringify(value)
      ) {
        return; // no-op
      }
      try {
        const { data } = await api.patch(
          `/work-orders/${woId}/diagnostic-report`,
          { [field]: value }
        );
        setReport(data);
        setErr("");
      } catch (e) {
        setErr(formatApiError(e));
      }
    },
    [draft, readOnly, report, woId]
  );

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/work-orders/${woId}/diagnostic-report/submit`
      );
      setReport(data);
      toast.success("Report submitted for review");
      const auditResp = await api.get(
        `/work-orders/${woId}/diagnostic-report/audit-log`
      );
      setAudit(auditResp.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/work-orders/${woId}/diagnostic-report/finalize`
      );
      setReport(data);
      toast.success("Report finalized");
      const auditResp = await api.get(
        `/work-orders/${woId}/diagnostic-report/audit-log`
      );
      setAudit(auditResp.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/work-orders/${woId}/diagnostic-report/reopen`,
        { note: reopenNote || null }
      );
      setReport(data);
      setReopenOpen(false);
      setReopenNote("");
      toast.success("Report reopened");
      const auditResp = await api.get(
        `/work-orders/${woId}/diagnostic-report/audit-log`
      );
      setAudit(auditResp.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const refreshPhotos = useCallback(async () => {
    try {
      const photosResp = await api.get(
        `/work-orders/${woId}/diagnostic-report/photos`
      );
      setPhotos(photosResp.data || []);
    } catch {
      /* ignore */
    }
  }, [woId]);

  const subtitle = useMemo(() => {
    if (!wo) return "Diagnostic report";
    return wo.property_address || "Diagnostic report";
  }, [wo]);

  if (!wo || !report) {
    return (
      <Layout title="Diagnostic Report" subtitle="Loading">
        <ErrorBanner message={err} />
      </Layout>
    );
  }

  return (
    <Layout title="Diagnostic Report" subtitle={subtitle}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link
          to={`/work-orders/${woId}`}
          data-testid="back-to-wo"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-amber-700"
        >
          <ArrowLeftIcon size={12} weight="bold" /> Back to Work Order
        </Link>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-data uppercase tracking-[0.18em] text-slate-500"
          title="Report ID"
        >
          report id · {report.id.slice(0, 8)}
          <CopyButton
            value={report.id}
            label="Report ID"
            testId="report-copy-id"
          />
        </span>
      </div>

      <ReportStatusBar
        status={report.status}
        canSubmit={canSubmit}
        canFinalize={canFinalize}
        canReopen={canReopen}
        busy={busy}
        onSubmit={submit}
        onFinalize={finalize}
        onReopen={() => setReopenOpen(true)}
      />

      <ErrorBanner message={err} />

      {report.status === "submitted" && (
        <div
          data-testid="banner-submitted"
          className="mt-4 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          Submitted{" "}
          {report.submitted_by_name
            ? `by ${report.submitted_by_name}`
            : ""}{" "}
          {report.submitted_at
            ? `· ${new Date(report.submitted_at).toLocaleString()}`
            : ""}
          {isOffice
            ? " — review and finalize, or edit before locking."
            : " — waiting for office to finalize. You can no longer edit."}
        </div>
      )}
      {report.status === "finalized" && (
        <div
          data-testid="banner-finalized"
          className="mt-4 mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-center justify-between gap-3 flex-wrap"
        >
          <div>
            Finalized{" "}
            {report.finalized_by_name
              ? `by ${report.finalized_by_name}`
              : ""}{" "}
            {report.finalized_at
              ? `· ${new Date(report.finalized_at).toLocaleString()}`
              : ""}
            . The report is now locked. Reopen to edit.
          </div>
          {isOffice && (
            <div className="flex items-center gap-2" data-testid="finalized-export-actions">
              <ReportPdfButton workOrderId={woId} />
              <PrintButton workOrderId={woId} />
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 mt-6">
        <div className="lg:col-span-2 space-y-8">
          <DiagnosticReportEditor
            draft={draft}
            onChange={setDraft}
            onCommitField={commitField}
            readOnly={readOnly}
          />

          <section>
            <h3 className="font-heading text-base font-semibold text-slate-900">
              Photos
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Field evidence — before, during, after. GPS EXIF stripped on
              upload for privacy.
            </p>
            {!readOnly && (
              <div className="mb-4">
                <PhotoUploader
                  workOrderId={woId}
                  api={api}
                  currentCount={photos.length}
                  onUploaded={refreshPhotos}
                  disabled={readOnly}
                />
              </div>
            )}
            <PhotoGrid
              photos={photos}
              api={api}
              canEdit={!readOnly}
              canDeletePhoto={!readOnly}
              onChange={refreshPhotos}
            />
          </section>
        </div>

        <aside className="space-y-6">
          <section
            data-testid="audit-section"
            className="bg-white border border-slate-200 rounded-md p-4"
          >
            <div className="font-heading text-sm font-semibold text-slate-900">
              Status History
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
              Append-only audit trail
            </div>
            <ReportAuditTrail entries={audit} />
          </section>

          {isOffice && report.status === "finalized" && (
            <section
              data-testid="share-section"
              className="bg-white border border-slate-200 rounded-md p-4"
            >
              <div className="font-heading text-sm font-semibold text-slate-900">
                Share with owner
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
                Tokenized read-only link · revocable
              </div>
              <ShareLinkPanel
                workOrderId={woId}
                reportStatus={report.status}
              />
            </section>
          )}

          {isOffice && (
            <FlagSidebarCard
              report={report}
              onChange={(updated) => setReport((r) => ({ ...r, ...updated }))}
            />
          )}

          {isOffice && (
            <RMARelatedCard reportId={report.id} workOrderId={woId} />
          )}

          {report.status === "finalized" && (
            <OwnerAcknowledgmentPanel report={report} />
          )}

          {isOffice && report.status === "finalized" && (
            <NotificationsMiniFeed reportId={report.id} />
          )}

          <section
            data-testid="meta-section"
            className="bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-700 space-y-1.5"
          >
            <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
              Linked Work Order
            </div>
            <div>
              <span className="text-slate-500">Customer:</span>{" "}
              {wo.customer_name || "—"}
            </div>
            <div>
              <span className="text-slate-500">Property:</span>{" "}
              {wo.property_address || "—"}
            </div>
            <div>
              <span className="text-slate-500">WO status:</span> {wo.status}
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => nav(`/work-orders/${woId}`)}
                className="text-[12px] font-semibold text-slate-700 hover:text-amber-700"
                data-testid="meta-open-wo"
              >
                Open work order →
              </button>
            </div>
          </section>
        </aside>
      </div>

      <Modal
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title="Reopen Report"
        subtitle="Office override"
        data-testid="reopen-modal"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reopen();
          }}
          className="space-y-4"
        >
          <Field label="Reason (optional)">
            <TextArea
              value={reopenNote}
              onChange={setReopenNote}
              rows={3}
              data-testid="reopen-note"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={() => setReopenOpen(false)}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={busy}
              data-testid="reopen-submit"
            >
              {busy ? "Reopening…" : "Reopen"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}



function FlagSidebarCard({ report, onChange }) {
  const flagged = !!report?.flagged;
  return (
    <section
      data-testid="flag-sidebar-card"
      className={`bg-white border rounded-md p-4 ${
        flagged ? "border-amber-300 bg-amber-50/40" : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-sm font-semibold text-slate-900">
            Follow-up flag
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Internal triage · owner / ops only
          </div>
          {flagged && report.flagged_at && (
            <div
              data-testid="flag-caption"
              className="text-[11px] text-slate-700 mt-2"
            >
              Flagged for follow-up
              {report.flagged_by_name ? ` by ${report.flagged_by_name}` : ""}
              {" · "}
              {_relative(report.flagged_at)}
            </div>
          )}
        </div>
        <FlagToggleButton
          reportId={report.id}
          flagged={flagged}
          flaggedAt={report.flagged_at}
          flaggedByName={report.flagged_by_name}
          testid="flag-toggle-detail"
          size={18}
          onChange={onChange}
        />
      </div>
    </section>
  );
}

function _relative(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
